import { ActionIcon, Button, Select, Tooltip } from "@common/components";
import { notification } from "@common/utils/notification";
import { timeToPixels } from "@common/utils/time-pixel-converter";
import { useDeepEffect } from "@common/utils/use-deep-effect";
import { Card, Loader, Portal, useMantineTheme } from "@mantine/core";
import { useElementSize, useViewportSize } from "@mantine/hooks";
import { updateProject } from "@requests/project/project";
import { UpdateProjectDto } from "@server/modules/project/project/dto";
import { Skeleton } from "@synqup/ui";
import { UseAsyncFormReturnType } from "@synqup/ui";
import axios from "axios";
import { Howl } from "howler";
import React, { useEffect, useState } from "react";
import { useDropzone } from "react-dropzone";
import { FaPenNib } from "react-icons/fa";
import { ChevronLeft } from "tabler-icons-react";

import {
  WaveformDragSeekInfo,
  WaveformLane,
} from "../../../../common/components/waveform/WaveformLane";
import {
  holdsProjectAudio,
  usePlayer,
  usePlayerState,
} from "../../../../contexts/Player";
import { stemHeight } from "../../../stemviewer/helpers/constants";
import { getBuffer } from "../../../stemviewer/recoil/helpers/stem";
import { useProject } from "../ProjectContext";

import { MixdownDescription } from "./components/MixdownDescription";
import { MixdownPanel } from "./components/MixdownPanel";
import {
  MixdownCompare,
  MixdownCompareVersions,
} from "./components/mixdown-compare/MixdownCompareVersions";
import { CreateMarkup } from "./components/mixdown-markup/CreateMarkup";
import { MixdownMarkup } from "./components/mixdown-markup/MixdownMarkup";

interface ProjectMixdownProps {
  form: UseAsyncFormReturnType<UpdateProjectDto>;
}

export const ProjectMixdown: React.FC<ProjectMixdownProps> = ({ form }) => {
  const {
    project,
    currMixdownId: projectCurrMixdownId,
    setCurrMixdownId: setProjectCurrMixdownId,
    markups,
    view,
    mutate,
  } = useProject();
  // Since create project page is not using project context
  const [_currMixdownId, _setCurrMixdownId] = useState<string | null>(null);
  const currMixdownId =
    projectCurrMixdownId === undefined ? _currMixdownId : projectCurrMixdownId;
  const setCurrMixdownId =
    projectCurrMixdownId === undefined
      ? _setCurrMixdownId
      : setProjectCurrMixdownId;
  const { seek, setVisible, unload } = usePlayer();
  const {
    currTime: _currTime,
    audio,
    playing: _playing,
  } = usePlayerState(["currTime", "audio", "playing"]);
  // Renamed from `isPlaying`, which is not what it means: this is only
  // "the player currently holds THIS project's audio", and it stays true
  // while paused. That name caused a real bug — it was handed to the
  // playhead as its playing flag, so the playhead kept gliding after pause.
  const currMixdown = project?.mixdowns?.find(
    (mixdown) => mixdown.id === currMixdownId
  );
  const isActiveAudio = holdsProjectAudio(
    audio,
    form.values.id,
    currMixdown?.file?.id
  );
  const currTime = isActiveAudio ? _currTime : 0;
  // The genuine transport state, which is what the playhead needs.
  const playing = isActiveAudio && _playing;
  const theme = useMantineTheme();
  const [loading, setLoading] = useState(false);
  const [buffer, setBuffer] = useState<AudioBuffer | null>(null);
  const { ref } = useElementSize();
  // Sourced from WaveformLane's own onMeasure rather than a parallel
  // useElementSize on this wrapper: the lane has no margin/padding of its
  // own, so its measured width IS the waveform's pixel width. Markups,
  // the region highlight and the progress overlay must position against
  // this exact number — a second, independently-computed width (the old
  // `width - 16`, tuned for the canvas's since-removed 8px margin) drifts
  // out of alignment with the waveform as soon as it disagrees.
  const [laneWidth, setLaneWidth] = useState(0);
  const [comparingMixdowns, setComparingMixdowns] = useState(false);
  const [compareMixdowns, setCompareMixdowns] =
    useState<Array<MixdownCompare> | null>(null);
  const [mixdownsLoaded, setMixdownsLoaded] = useState(0);
  const [region, setRegion] = useState<[number, number] | null>(null);
  const [createMarkup, setCreateMarkup] = useState(false);
  const { width: vpWidth } = useViewportSize();
  const isMobile = vpWidth < theme.breakpoints.md;

  const { isDragActive, getRootProps, getInputProps, inputRef } = useDropzone({
    accept: "audio/mp3,audio/mpeg,audio/wav,audio/ogg,audio/flac,audio/aac",
    multiple: false,
    noClick: true,
    onDrop: async (files) => {
      setLoading(true);
      const file = files[0];
      unload();
      setCurrMixdownId(null);

      const reader = new FileReader();

      reader.onload = async (e) => {
        const buffer = await getBuffer(e.target?.result as ArrayBuffer);

        form.setFieldValue("file", file as any);
        form.setFieldValue("mixdown", {
          duration: buffer.duration,
        } as any);

        if (project?.id) {
          const { error } = await updateProject(project.id, {
            file: file,
            mixdown: {
              duration: buffer.duration,
            },
          });

          if (error) {
            notification.error(error.message);
          } else {
            await mutate();
          }
        }

        setLoading(false);
        setBuffer(buffer);
      };

      reader.onerror = console.error;

      reader.readAsArrayBuffer(file);
    },
  });

  // Set mixdown when project loaded
  useEffect(() => {
    if (currMixdownId) return;

    if (project?.mixdowns && project.mixdowns.length > 0) {
      setCurrMixdownId(project.mixdowns[0].id);
    }
  }, [project?.mixdowns]);

  // Load mixdown from file if it exists
  useDeepEffect(() => {
    if (!currMixdown?.file?.url) return;

    setLoading(true);

    axios
      .get(currMixdown.file.url, {
        responseType: "blob",
      })
      .then((response) => {
        return new File([response.data], form.values.name, {
          type: "audio/mp3",
        }).arrayBuffer();
      })
      .then((arrayBuffer) => getBuffer(arrayBuffer))
      .then((buffer) => {
        setBuffer(buffer);
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  }, [currMixdown]);

  // Play audio whenever user drags or clicks the waveform. WaveformLane
  // reports every drag frame (and a plain click, once, as a synthetic frame
  // with movementX === 0 and down === false) via onDragSeek instead of
  // seeking on its own, because a drag here can mean two different things:
  // a small movement (or a click) scrubs, a movement beyond +/-15px draws a
  // markup selection region instead. This mirrors the branching the old
  // canvas-based drag handler used, just driven by the lane's own gesture
  // reporting rather than reading `document.getElementById("mixdown_canvas")`.
  const handleDragSeek = ({
    time,
    startTime,
    movementX,
    down,
  }: WaveformDragSeekInfo) => {
    // A drag-release frame (down === false with nonzero movement) carries no
    // new information — the last down:true frame already handled it, same
    // as the old handler which ignored `!down` entirely. Only a genuine
    // click (down === false, movementX === 0) needs to be handled here,
    // since it never produces a down:true frame at all.
    const isClick = !down && movementX === 0;
    if (!down && !isClick) return;

    // Hide create markup when dragging
    setCreateMarkup(false);

    // Selecting region
    if (!isMobile && (movementX > 15 || movementX < -15)) {
      if (time < startTime) {
        setRegion([time, startTime]);
      } else {
        setRegion([startTime, time]);
      }

      if (isActiveAudio) seek(startTime);
      return;
    }

    // Unsetting region
    if (region && (time < region[0] || time > region[1])) {
      setRegion(null);
    }

    if (isActiveAudio) seek(time);
  };

  // Hide player if playing the mixdown
  useEffect(() => {
    if (audio?.projectId === form.values.id) {
      setVisible(false);
    }

    return () => {
      if (audio) setVisible(true);
    };
  }, [audio]);

  const handleOpen = () => {
    inputRef.current?.click();
  };

  const handleMixdownChange = async (id: string) => {
    if (id === currMixdownId) return;
    unload();
    setCurrMixdownId(id);
  };

  const handleMixdownCompare = async (compareMixdownId: string) => {
    const compareMixdown = project?.mixdowns.find(
      (mixdown) => mixdown.id === compareMixdownId
    );

    if (compareMixdownId === currMixdownId) return;
    if (!compareMixdown?.file?.url) return;
    if (!currMixdown?.file?.url) return;
    if (!buffer) return;

    setComparingMixdowns(true);

    // No longer fetches/decodes an AudioBuffer for the comparison mixdown
    // here: MixdownCompareVersions' WaveformLane draws from usePeaks now,
    // not a decoded buffer, so that network round-trip only added latency
    // to opening the compare view with nothing left to consume its result.
    unload();
    setCompareMixdowns([
      {
        ...compareMixdown,
        howl: new Howl({
          src: compareMixdown.file.url,
          format: ["webm", "mp3"],
          volume: 0,
          onload: () => setMixdownsLoaded((loaded) => loaded + 1),
          onloaderror: console.log,
        }),
        selected: false,
      },
      {
        ...currMixdown,
        howl: new Howl({
          src: currMixdown.file.url,
          format: ["webm", "mp3"],
          volume: 1,
          onload: () => setMixdownsLoaded((loaded) => loaded + 1),
          onloaderror: console.log,
        }),
        selected: true,
      },
    ]);
  };

  const cancelCompareMixdown = () => {
    setMixdownsLoaded(0);
    setComparingMixdowns(false);
    setCompareMixdowns(null);
  };

  const closeCompareMixdown = () => {
    setMixdownsLoaded(0);
    setComparingMixdowns(false);
    setCompareMixdowns(null);
  };

  if (loading) return <Skeleton visible height={149} />;

  // No mixdowns uploaded yet
  if (!buffer)
    if (view !== "viewer")
      return (
        <Card
          data-quick-assist-id="project-mixdown"
          ref={ref}
          style={{
            border: isDragActive
              ? `2px dashed ${theme.colors.indigo[5]}`
              : `2px solid ${theme.colors.dark[6]}`,
          }}
          className="mb-6 p-1 shadow-2xl"
          {...getRootProps()}
        >
          <div
            className="flex justify-center items-center gap-4"
            style={{
              margin: 8,
              height: stemHeight,
            }}
          >
            <p className="text-dark-400 m-0">Drag and drop your demo here</p>
            <Button size="xs" variant="light" onClick={handleOpen}>
              Upload
            </Button>
            <input
              data-testid="project-mixdown-upload-input"
              {...getInputProps()}
            />
          </div>
        </Card>
      );
    else return null;

  // Comparing mixdowns
  if (comparingMixdowns)
    return (
      <div>
        {compareMixdowns && mixdownsLoaded === 2 ? (
          <MixdownCompareVersions
            mixdowns={compareMixdowns}
            onClose={closeCompareMixdown}
          />
        ) : (
          <div className="relative flex items-center justify-center gap-4 h-40">
            <Button
              leftIcon={<ChevronLeft className="w-3 h-3" />}
              variant="light"
              color="gray"
              size="xs"
              className="absolute right-2 top-2 z-30"
              onClick={cancelCompareMixdown}
            >
              Back
            </Button>

            <Loader size="sm" />
            <p className="text-center text-dark-400">Loading mixdowns...</p>
          </div>
        )}
      </div>
    );

  return (
    <div>
      {project?.mixdowns && project.mixdowns.length > 0 && (
        <Portal target={document.getElementById("project-hotbar")!}>
          <Select
            data-quick-assist-id="mixdown-select"
            className="w-32 md:w-48"
            classNames={{ input: "my-0" }}
            size="sm"
            data={
              project.mixdowns?.map((mixdown) => ({
                label: mixdown.name,
                value: mixdown.id,
              })) || []
            }
            value={currMixdown?.id}
            onChange={(value) => {
              unload();
              if (value) handleMixdownChange(value);
            }}
          />
        </Portal>
      )}

      <Card
        className="p-1 shadow-2xl"
        style={{
          background: `linear-gradient(90deg, ${theme.colors.indigo[7]}, ${theme.colors.rose[7]})`,
        }}
        data-testid="project-mixdown"
        data-quick-assist-id="project-mixdown"
      >
        <div {...getRootProps()} onFocus={(e) => e.target.blur()}>
          <div
            style={{
              position: "relative",
              touchAction: "none",
            }}
            ref={ref}
          >
            {/* Not gated on `currMixdown?.file`: this component also serves
                the create-project page, where the demo has been dropped into
                the form but not uploaded, so there is no File entity and
                never any precomputed peaks for it. Gating here made the lane
                vanish on that page, collapsing the card to a bare gradient
                strip. The lane draws from `buffer` in that case — which is
                what the pre-redesign canvas did there — and swaps to the
                real peaks once a file exists and they're ready. `buffer` is
                always set by this point: the `if (!buffer)` return above. */}
            <WaveformLane
              file={currMixdown?.file}
              buffer={buffer}
              currentTime={currTime}
              playing={playing}
              duration={buffer.duration}
              onSeek={(time) => {
                if (isActiveAudio) seek(time);
              }}
              onDragSeek={handleDragSeek}
              onMeasure={setLaneWidth}
            >
              {/* Same `left` fix as MixdownMarkup.tsx: this used to carry
                    a `+ 9` fudge bridging the old canvas's 8px margin. This
                    highlight is a child of WaveformLane now, so `region[0]`
                    maps to the same x as the playhead at that time with no
                    bridge needed — the width calc never had a matching
                    deduction, so it's untouched. */}
              {!isMobile && region && (
                <div
                  className="absolute -top-2 h-[110%] bg-blue-400 opacity-80 mix-blend-color-dodge rounded"
                  style={{
                    left: timeToPixels(region[0], laneWidth, buffer.duration),
                    width: timeToPixels(
                      region[1] - region[0],
                      laneWidth,
                      buffer.duration
                    ),
                  }}
                />
              )}

              {/* Not a marker/position indicator — a button positioned
                    with a fixed 16px (1rem) clearance from the current
                    time/region-end so it doesn't sit directly on top of the
                    playhead. Reviewed against the same coordinate-origin
                    change as the fixes above and left unchanged: 16 is a
                    plain Tailwind spacing unit (matches -left-4/px-4 just
                    below), not a leftover canvas-margin bridge. */}
              {/* Hidden until the pointer is inside the lane, so the
                  waveform reads clean at rest. Driven entirely by
                  WaveformLane's `group/lane` hover rather than React state:
                  an opacity transition is composited, so this costs nothing
                  per frame and — unlike the `createMarkupHovered` state it
                  replaces — triggers no re-render on every pointer
                  enter/leave of a component that already re-renders on each
                  playback tick.

                  `pointer-events-none` while hidden matters as much as the
                  opacity: an invisible button still swallows clicks, and
                  this one sits directly over the lane's seek/scrub surface.

                  The fade is skipped once `createMarkup` is open — that form
                  extends past the lane's edges, so fading on lane-exit would
                  dismiss it from under the pointer mid-typing. */}
              <div
                className={`absolute z-50 top-1/2 -translate-y-1/2${
                  createMarkup
                    ? ""
                    : " opacity-0 pointer-events-none transition-opacity duration-200 ease-out group-hover/lane:opacity-100 group-hover/lane:pointer-events-auto"
                }`}
                style={{
                  left:
                    timeToPixels(
                      region ? region[1] : currTime,
                      laneWidth,
                      buffer.duration
                    ) + 16,
                }}
              >
                {isMobile || view === "viewer" ? null : createMarkup ? (
                  <CreateMarkup
                    start={region ? region[0] : currTime}
                    end={region ? region[1] : undefined}
                    onClose={() => setCreateMarkup(false)}
                  />
                ) : (
                  // A plain wrapper carries the resting translucency and its
                  // own hover, rather than putting these classes on
                  // ActionIcon: Mantine injects its emotion styles at
                  // runtime, after the Tailwind sheet, so at equal
                  // specificity Mantine's own `transition` on the button
                  // would win and the fade would snap instead of easing.
                  // Nesting sidesteps the ordering question entirely.
                  // Opacity multiplies down the tree: outside the lane
                  // 0 x 0.5 = hidden, inside 1 x 0.5 = translucent at rest,
                  // over the button 1 x 1 = solid — the same two-step
                  // appearance as before, without the state that drove it.
                  <div className="inline-flex opacity-50 hover:opacity-100 transition-opacity duration-150 ease-out">
                    <Tooltip label="Add markup">
                      <ActionIcon
                        data-testid="sv-create-markup"
                        data-quick-assist-id="sv-create-markup"
                        variant="filled"
                        color="dark"
                        onClick={() => setCreateMarkup(true)}
                        onPointerDown={(e) => e.stopPropagation()}
                      >
                        <FaPenNib className="w-4 h-4 text-red-500" />
                      </ActionIcon>
                    </Tooltip>
                  </div>
                )}
              </div>

              {/* The two-tone progress fill that used to live here is gone.
                    Requirement 2 replaces it with WaveformLane's playhead as
                    the indicator of playback position, and its 200ms width
                    transition is exactly the easing requirement 5 forbids. */}

              {markups
                ?.filter(
                  (markup) =>
                    markup.mixdownId &&
                    currMixdownId &&
                    markup.mixdownId === currMixdownId
                )
                ?.map((markup) => (
                  <MixdownMarkup
                    key={markup.id}
                    markup={markup}
                    duration={buffer.duration}
                    width={laneWidth}
                  />
                ))}
            </WaveformLane>

            <input
              data-testid="project-mixdown-upload-input"
              {...getInputProps()}
            />
          </div>
        </div>
      </Card>

      {buffer && (
        <MixdownPanel
          buffer={buffer}
          currMixdown={currMixdown}
          project={form.values}
          file={form.values.file}
          onUpload={handleOpen}
          onMixdownChange={handleMixdownChange}
          onMixdownCompare={handleMixdownCompare}
        />
      )}

      <div className="-mt-2">
        {currMixdown && <MixdownDescription mixdown={currMixdown} />}
      </div>
    </div>
  );
};
